import { useMap } from '@vis.gl/react-google-maps';
import { useEffect, useRef } from 'react';

/**
 * Renders vertical lines with local time labels across the map
 * at each UTC hour offset (every 15° of longitude).
 */
const TimeZoneMarkers = ({ enabled = true }: { enabled?: boolean }) => {
  const map = useMap();
  const overlaysRef = useRef<google.maps.Polyline[]>([]);
  const markersRef = useRef<google.maps.Marker[]>([]);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    if (!map) return;

    const update = () => {
      // Clear previous
      overlaysRef.current.forEach((l) => l.setMap(null));
      markersRef.current.forEach((m) => m.setMap(null));
      overlaysRef.current = [];
      markersRef.current = [];

      if (!enabled) return;

      const now = new Date();
      const utcHours = now.getUTCHours();
      const utcMinutes = now.getUTCMinutes();

      // Place a line + label every 15° of longitude (= 1 hour)
      for (let offsetHours = -12; offsetHours <= 12; offsetHours++) {
        const lng = offsetHours * 15;

        const localHour = ((utcHours + offsetHours) % 24 + 24) % 24;
        const timeStr = `${localHour.toString().padStart(2, '0')}:${utcMinutes.toString().padStart(2, '0')}`;
        const utcLabel = offsetHours === 0
          ? 'UTC'
          : `UTC${offsetHours > 0 ? '+' : ''}${offsetHours}`;

        // Vertical dashed line
        const line = new google.maps.Polyline({
          path: [
            { lat: -85, lng },
            { lat: 85, lng },
          ],
          strokeColor: offsetHours === 0 ? '#FFD700' : '#FFFFFF',
          strokeOpacity: offsetHours === 0 ? 0.5 : 0.2,
          strokeWeight: offsetHours === 0 ? 2 : 1,
          map,
        });

        // Time label at the top
        const marker = new google.maps.Marker({
          position: { lat: 75, lng },
          map,
          icon: {
            url: 'data:image/svg+xml,' + encodeURIComponent(createLabelSvg(timeStr, utcLabel, offsetHours === 0)),
            anchor: new google.maps.Point(30, 15),
          },
          clickable: false,
          zIndex: 100,
        });

        overlaysRef.current.push(line);
        markersRef.current.push(marker);
      }
    };

    update();
    intervalRef.current = setInterval(update, 60_000);

    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
      overlaysRef.current.forEach((l) => l.setMap(null));
      markersRef.current.forEach((m) => m.setMap(null));
    };
  }, [map, enabled]);

  return null;
};

export default TimeZoneMarkers;

function createLabelSvg(time: string, utcLabel: string, isUtc: boolean): string {
  const bgColor = isUtc ? 'rgba(255,215,0,0.85)' : 'rgba(0,0,0,0.7)';
  const textColor = isUtc ? '#000' : '#fff';
  const borderColor = isUtc ? '#FFD700' : 'rgba(255,255,255,0.3)';

  return `<svg xmlns="http://www.w3.org/2000/svg" width="60" height="30">
    <rect x="0" y="0" width="60" height="30" rx="4" fill="${bgColor}" stroke="${borderColor}" stroke-width="1"/>
    <text x="30" y="12" text-anchor="middle" font-family="Arial,sans-serif" font-size="10" font-weight="bold" fill="${textColor}">${time}</text>
    <text x="30" y="24" text-anchor="middle" font-family="Arial,sans-serif" font-size="8" fill="${textColor}" opacity="0.7">${utcLabel}</text>
  </svg>`;
}
